import React, { Suspense, useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./app.scss";
import Login from "./screens/login/login";
import PlatformLayout from "./Layout";
import "./styles/bs-utilities.css";
import CustomerLogin from "./screens/customerlogin/customerlogin";
import { AppConfig } from "./utils/ApiConstants";
import CommonLandingPage from "./screens/CommonLandingPage";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from "react-i18next";
import { GetRequest } from "./utils/api";
import { triggerLangChange } from "./vernacular";
import CustomLoader from "./components/loader";
import Redirect from "./screens/redirect/redirect";
import { MenuContext } from "./MenuProvider";
import { isTokenExpired } from "./utils/object";


const DashBoardLandingPage = React.lazy(() => import("./screens/dashboards/dashBoardLandingPage"));
const AnalyticsLandingPage = React.lazy(() => import("./screens/analytics/analyticsLandingPage"));
const ForgotPassword = React.lazy(() => import("./screens/forgotPassword/forgotPassword"));
const ResetPasswordPage = React.lazy(() => import("./screens/resetPassword/resetPassword"));
const ChangePassword = React.lazy(() => import("./screens/changePassword/changePassword"));
const MastersTable = React.lazy(() => import("./screens/mastersTables/mastersTable"));
const HomePage = React.lazy(() => import("./screens/homePage/homePage"));

interface Props { }

const App: React.FC<Props> = () => {

  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [showSideDrawer, setShowSideDrawer] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<any>(false);

  const unProtectedRoutes: string[] = ["login", "redirect", "customerlogin", "forgotpassword", "resetpassword"];


  useEffect(() => {

    const handleLogout = () => {
      navigate('/login');
    };

    window.removeEventListener('logout', handleLogout);
    window.addEventListener('logout', handleLogout);

  }, []);

  
  //Handling right click - disabling
  useEffect(() => {

    const handleContextmenu = (e: Event) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
    }

    if (AppConfig.RIGHT_CLICK_DISABLED) {
      document.addEventListener('contextmenu', handleContextmenu)
    }

  }, []);

  const [loading, setLoading] = useState<boolean>(true);
  const [menuLinks, setMenuLinks] = useState<any>();
  const { menuItems, setMenuItems } = useContext(MenuContext);

  function containsAny(stringToCheck: string, arrayToCheck: string[]): boolean {
    for (const element of arrayToCheck) {
      if (stringToCheck.includes(element)) {
        return true;
      }
    }
    return false;
  }

  useEffect(() => {

    if (menuItems.length > 0) {
      setMenuLinks(menuItems);
      setLoading(false);
    }

  }, [menuItems]);


  useEffect(() => {

    if (menuLinks === null || menuLinks === undefined || menuLinks.length === 0) {

      if (localStorage.getItem("user") != null && !isTokenExpired(JSON.parse(localStorage.getItem("user") || "{}")?.accessToken)) {

        if (menuItems.length > 0) {

          if (location.pathname && location.pathname !== "/login") {
            navigate(location.pathname);
          } else {
            navigate(`/${menuItems[0].mainMenuCode}`);
          }

        } else {

          GetRequest('users/menulinks').then((res) => {

            setMenuLinks(res.records);
            setMenuItems(res.records);
            setLoading(false);

            if (location.pathname && location.pathname !== "/login") {
              navigate(location.pathname);
            } else {
              navigate(`/${res.records[0].mainMenuCode}`);
            }
          });

        }

      } else if (!containsAny(location.pathname, unProtectedRoutes)) {
        navigate("/login");
      }
    } else {
      setLoading(false);
    }

  }, []);


  useEffect(() => {
    //Removing - event listner before adding - to avoid duplicate event listeners
    window.removeEventListener("languageChanged", handleLangChange);
    window.addEventListener("languageChanged", handleLangChange);

  }, []);


  const handleLangChange = (ev: any) => {

    if (ev.detail.blueprintAPI) {

      GetRequest(ev.detail.blueprintAPI).then((res) => {

        fetch(res.downloadUrl) // Using fetch here instead of GetRequest- as it is a call to aws s3 bucket url 
          .then(response => response.arrayBuffer())
          .then(buffer => {
            const decoder = new TextDecoder();
            const text = decoder.decode(buffer);

            i18n.addResources(ev.detail.lang, 'translation', JSON.parse(text));
            i18n.changeLanguage(ev.detail.lang);
          });
      });

    } else { // If blueprint API is not defined - fallback to default language
      i18n.changeLanguage("en");
    }

  };



  //Handling webview language 
  function extractLanguageCode(input: string): string | null {

    const langPrefix = "lang:";
    const langIndex = input.indexOf(langPrefix);

    if (langIndex !== -1) {
      return input.substring(langIndex + langPrefix.length, langIndex + langPrefix.length + 2);
    }

    return null;
  }

  const userAgent = navigator.userAgent || "";
  const langCode = extractLanguageCode(userAgent);

  useEffect(() => {
    if (langCode && langCode?.length > 0) {
      triggerLangChange(langCode);
    }
  }, []);

  return (
    <div>
      <Routes>
        {!loading && <Route
          path="/"
          element={
            <PlatformLayout
              showSideDrawer={showSideDrawer}
              isShowSideDrawer={(value: any) => setShowSideDrawer(value)}
              visible={showSideDrawer}
              setVisible={(val: any) => setShowSideDrawer(val)}
              setIsDarkMode={setIsDarkMode}
              darkMode={isDarkMode}
            />
          }
        >


          {menuLinks.map((menu: any) => {

            if (menu.mainMenuCode === "home") {
              return <Route path="home">
                <Route index path="*" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}><HomePage subMenu={menuLinks.find((item: any) => item.mainMenuCode === "home")} /></Suspense>} />
              </Route>
            } else if (menu.mainMenuCode === 'analytics') {
              return <Route path="analytics">
                <Route index path="*" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}> <AnalyticsLandingPage subMenu={menuLinks.find((item: any) => item.mainMenuCode === "analytics")} /> </Suspense>} />
              </Route>
            }
            else if (menu.mainMenuCode === 'dashboards') {
              return <Route path="dashboards">
                <Route index path="*" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}> <DashBoardLandingPage subMenu={menuLinks.find((item: any) => item.mainMenuCode === "dashboards")} /> </Suspense>} />
              </Route>
            } else if (menu.mainMenuCode === "masters") {
              return <Route path="masters">
                <Route index path="*" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}> <MastersTable subMenu={menuLinks.find((item: any) => item.mainMenuCode === "masters")} /> </Suspense>} />
              </Route>
            } else {
              return <Route path={menu.mainMenuCode}>
                <Route index path="*" element={<CommonLandingPage subMenu={menu} />} />
              </Route>
            }

          })}
          <Route path="home">
            <Route index path="*" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}><HomePage subMenu={menuLinks.find((item: any) => item.mainMenuCode === "home")} /></Suspense>} />
          </Route>
          <Route path="changePassword" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}><ChangePassword /></Suspense>} />
        </Route>}

        <Route path="/login" element={<Login />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/customerlogin" element={<CustomerLogin />} />
        <Route path="/forgotpassword" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}><ForgotPassword /> </Suspense>} />
        <Route path="/resetpassword/" element={<Suspense fallback={<div className="text-center py-5"> <CustomLoader tip={"Loading..."} size="lg"></CustomLoader> </div>}><ResetPasswordPage /></Suspense>} />


      </Routes>
      <ToastContainer />
    </div>
  );
};

export default React.memo(App);




